import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { useDelayedVisible } from './useDelayedVisible';

/**
 * Blocking work: a query with nothing cached yet (first visit, new filter, new
 * route) or a write in flight. The user is staring at an empty or stale screen
 * until it lands, so it warrants the top progress bar.
 */
export function useIsBlockingLoad(): boolean {
  const coldFetches = useIsFetching({ predicate: (query) => query.state.data === undefined });
  const mutations = useIsMutating();
  return useDelayedVisible(coldFetches + mutations > 0);
}

/**
 * Background work: refetches of data already on screen — the polling intervals
 * on agents, sessions, host stats, and friends. Only worth a quiet marker, and
 * only when the round trip is slow enough to be noticeable.
 */
export function useIsBackgroundSyncing(): boolean {
  const allFetches = useIsFetching();
  const coldFetches = useIsFetching({ predicate: (query) => query.state.data === undefined });
  return useDelayedVisible(allFetches - coldFetches > 0, { delayMs: 600, minVisibleMs: 600 });
}
